import React from 'react'
import { usePageView } from '@fs/zion-analytics'
import { LayoutBand, FlowGrid, useContainerWidth, MarginBox, H1, Separator } from '@fs/zion-ui'
import { usePermission } from '@fs/zion-permissions'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useTopicContext } from '../../providers/TopicProvider'
import getStaticActivities from './getStaticActivities'
import { useGetCampaigns } from '../../hooks'
import processCampaigns from '../../helpers/campaignUtil'
import { useUserDetailsContext } from '../../providers/UserDetailsProvider'
import KidsCard from '../../components/KidsCard'
import { usePWAMode } from '../../hooks/appRootHooks'
import useExternalLinks from '../../hooks/useExternalLinks'
import getIsTWAMode from '../../helpers/appMode_helper'
import { enterAnimationCss, enterDelay } from '../../helpers/enterAnimation'
import useEnterAnimationRun from '../../hooks/useEnterAnimationRun'

const Activities = () => {
  const [t] = useTranslation()
  const initData = { page_detail: 'Activities' }
  const [isMember] = usePermission('MemberContentPermission', 'DiscoveryUserContext')
  const { signedIn } = useUser()
  const { data } = useGetCampaigns({ enabled: signedIn, isMember })
  const { showRoman } = useUserDetailsContext()
  const processedCampaigns = processCampaigns(data?.person?.campaigns, isMember)
  const atWidth = useContainerWidth()
  const columnCount = atWidth({ default: 2, md: 3, xl: 4, xxl: 6 })
  usePageView(initData)
  const { topicId } = useTopicContext()
  const isPWA = usePWAMode()
  const externalActivitiesFlag = useFeatureFlag('discovery_togetherExternalActivities')
  const staticActivities = getStaticActivities(isPWA, externalActivitiesFlag.isOn)
  const allActivities = [...staticActivities, ...processedCampaigns]
  useExternalLinks()

  const animateEntrance = getIsTWAMode()

  // Closing an activity brings this page back without remounting it, so the cards are re-keyed on each
  // return to make the entrance play again rather than the tab simply reappearing.
  const enterRun = useEnterAnimationRun(animateEntrance)

  return (
    <LayoutBand>
      <MarginBox marginY="md">
        <H1 size="H3" centered>
          {t('nav.activities', 'Activities')}
        </H1>
        <Separator />
        <FlowGrid columnCount={columnCount}>
          {allActivities?.map((card, index) => {
            return (
              <div
                key={`div_${enterRun}_${card?.person?.personId ?? card?.activityGroupId ?? card?.id}`}
                css={animateEntrance ? enterAnimationCss : undefined}
                style={{
                  justifyContent: 'center',
                  display: 'flex',
                  ...(animateEntrance ? enterDelay(index) : {}),
                }}
              >
                <KidsCard
                  topicId={topicId}
                  key={card?.person?.personId ?? card?.activityGroupId ?? card?.id}
                  data={{ ...card }}
                  showRoman={showRoman}
                />
              </div>
            )
          })}
        </FlowGrid>
        <Separator />
      </MarginBox>
    </LayoutBand>
  )
}

export default Activities
